'use client';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useRouter } from '@/navigation';
import { mutate } from 'swr';
import type { Entry } from 'contentful';

import { ExtendedProductFields } from '@/core/contentfulConfig';
import { addToCart } from '@/lib/cart.browser';
import { TW_PRODUCT_MERCH_CARD_CLASSES } from '@/core/constants';
import { Button, Badge, HeaderText, LuvImage } from '@/components/ui';
import { HeaderSize } from '@/components/ui/HeaderText';

import { LearnMoreModal } from '@/components/common';

import { TypeHolidayBundlesCardFields } from '@/core/generated-types';
import {
  generateAnalyticsCartData,
  generateNameId,
  getBundleAvailability,
  generateCartItems,
  redirectAfterAddToCart,
  generatePlayKitchenItem,
  generateBundleData,
} from './utils';
import { CTA_COLORS } from '../types';
import useLocale from '@/hooks/useLocale';
import { getLocaleInfo } from '@/utils/helpers';
import { trackAddToCart } from '@/utils/subflowAnalyticsHelpers';
import { convertPriceToFloat } from '@/utils/currency';
import { calculatePercentage } from '@/utils/calculations';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  PRODUCT_EVENT_TYPES,
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_ADDED_PAGE_TYPES,
  trackItemClickedEvent,
  trackProductEvent,
} from '@/utils/rudderstack';
import { usePathname } from 'next/navigation';

function HolidayBundlesCard({
  title,
  image,
  description,
  learnMoreDescription,
  learnMoreButtonText,
  bundleImages,
  price,
  maxPrice,
  originalPrice,
  includes,
  buttonText,
  includesLabel,
  badge,
  includePlayKits,
  includeBookBundles = false,
  outOfStock,
  outOfStockButtonText = 'Sold out',
  ...props
}: TypeHolidayBundlesCardFields) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);

  const router = useRouter();
  const pathname = usePathname();
  const language = useLocale();
  const localeInfo = getLocaleInfo(language);

  const playKitchenProduct = includes.find(
    (item) =>
      item.fields.shopifyProduct?.tags.includes('the-play-kitchen') &&
      item.fields.shopifyProduct?.variants?.length > 1,
  ) as Entry<ExtendedProductFields> | undefined;

  const variants =
    (playKitchenProduct &&
      playKitchenProduct.fields?.shopifyProduct?.variants) ??
    [];
  const [selectedVariant, setSelectedVariant] = useState(variants?.[0]);

  const isBundleUnavailable = outOfStock
    ? outOfStock
    : getBundleAvailability(includes);

  const priceAsFloat = convertPriceToFloat(price);
  const originalPriceAsFloat = convertPriceToFloat(originalPrice);
  const discount =
    calculatePercentage(priceAsFloat, originalPriceAsFloat) / 100;

  const pricingCopy = maxPrice ? `${price} - ${maxPrice}` : `${price}`;
  const quantity = 1;

  const analyticsBundleData = (source: 'robust-product-card' | 'modal') => {
    const bundleData = generateBundleData({
      name: title,
      playKitsQuantity: Number(includePlayKits || 0),
      includeBookBundles,
    });

    return {
      url: pathname || '',
      productId: bundleData.sku,
      sku: bundleData.sku,
      category: bundleData.category,
      name: title,
      ageRange: bundleData.ageRange,
      ageUnits: '',
      price: priceAsFloat || 0,
      quantity,
      component: source,
      pageType: PRODUCT_ADDED_PAGE_TYPES.LANDING_PAGE,
    };
  };

  const analyticsCartData = (source: 'product-card' | 'modal') => {
    return generateAnalyticsCartData({
      currency: localeInfo.currencyCode,
      name: title,
      totalPrice: priceAsFloat || 0,
      discount,
      items: includes.filter(
        (item: any) => item.sys.contentType.sys.id === 'product',
      ) as Entry<ExtendedProductFields>[],
      source,
      quantity,
      selectedVariant,
      playKitsQuantity: Number(includePlayKits || 0),
      includeBookBundles,
    });
  };

  const onAddToCart = async () => {
    if (playKitchenProduct) {
      setIsOpen(true);
      return;
    }

    setLoading(true);

    const cartItems = generateCartItems(includes, quantity);

    try {
      const response = await addToCart(cartItems, quantity, { noredirect: 1 });
      if (!response.ok) {
        throw new Error('addToCart failed to execute');
      }

      mutate('/cart', { revalidate: true });
      trackAddToCart(
        analyticsCartData(PRODUCT_ADDED_COMPONENT_TYPES.PRODUCT_CARD),
      );
      trackProductEvent(
        PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
        analyticsBundleData(PRODUCT_ADDED_COMPONENT_TYPES.ROBUST_PRODUCT_CARD),
      );

      setLoading(false);
      redirectAfterAddToCart(
        router,
        includePlayKits,
        includeBookBundles,
        language,
      );
    } catch (error) {
      console.error('Error adding products to cart:', error);
      setLoading(false);
    }
  };

  const onModalAddToCart = async () => {
    setLoadingModal(true);
    const cartItems = generateCartItems(includes, quantity);
    const playKitchenItem = generatePlayKitchenItem(selectedVariant, quantity);

    const bundleItems = playKitchenProduct ? [playKitchenItem] : [...cartItems];

    try {
      const response = await addToCart(bundleItems, quantity, {
        noredirect: 1,
      });
      if (!response.ok) {
        throw new Error('addToCart failed to execute');
      }

      mutate('/cart', { revalidate: true });
      trackAddToCart(analyticsCartData(PRODUCT_ADDED_COMPONENT_TYPES.MODAL));
      trackProductEvent(
        PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
        analyticsBundleData(PRODUCT_ADDED_COMPONENT_TYPES.MODAL),
      );

      setLoadingModal(false);
      redirectAfterAddToCart(
        router,
        includePlayKits,
        includeBookBundles,
        language,
      );
    } catch (error) {
      console.error('Error adding products to cart:', error);
      setLoadingModal(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      trackItemClickedEvent({
        url: window.location.href,
        type: CLICKED_ITEM_TYPES.LINK,
        cta: CTA_NAMES.BFCM_2024_BUNDLE_LEARN_MORE,
        // Intentionally send bundle name for page location for BFCM
        pageLocation: title as unknown as PAGE_LOCATIONS,
      });
    }
  }, [isOpen, title]);

  return (
    <>
      <div
        className={classNames(
          'rounded bg-white shadow-md flex flex-col',
          TW_PRODUCT_MERCH_CARD_CLASSES.container,
        )}
        data-ga-id={generateNameId(title)}
        data-ga-section="product-card"
        data-ga-name={title}
        data-ga-price={priceAsFloat}
        data-ga-category="BFCM Bundle"
        data-ga-currency-code={localeInfo.currencyCode}
        data-ga-list-name="bfcm bundles"
        data-ga-variant-title="Bundles"
        {...props}
      >
        <div className="relative">
          {badge && (
            <div className="absolute left-4 top-5 z-10">
              <Badge {...badge.fields} />
            </div>
          )}
          <figure className="rounded-t">
            <LuvImage
              height={240}
              width={360}
              imageData={image}
              alt={image?.fields?.altText || ''}
              className={classNames(
                'w-full rounded-t',
                TW_PRODUCT_MERCH_CARD_CLASSES.image,
              )}
            />
          </figure>
        </div>
        <div className="mx-6 flex flex-col grow justify-between">
          <div className="mt-6">
            <HeaderText size={HeaderSize.H3} text={title} align="left" />
            <div className="text-left mt-2 mb-3">
              <p className="m-0 inline text-base">{description}</p>
              <button
                className="inline underline font-bold ml-1 text-sm md:text-base"
                onClick={() => setIsOpen(true)}
              >
                {learnMoreButtonText}
              </button>
            </div>
            {includes && (
              <div
                className="flex grow flex-col"
                data-testid="product-includes-container"
              >
                <p className="text-left text-lg font-bold">{includesLabel}</p>
                {includes.map((item) => {
                  return (
                    <ul
                      className="mt-2 pl-[14px] list-disc flex items-start text-left"
                      key={item.sys.id}
                    >
                      {item.sys.contentType.sys.id ===
                        'iconShortDescription' && (
                        <li className="text-base">{item.fields.description}</li>
                      )}
                    </ul>
                  );
                })}
              </div>
            )}
          </div>
          <div className="mt-4 mb-6">
            <div className="flex items-baseline gap-2 mb-3">
              <p className="text-h-x4 font-bold text-red-v2">{pricingCopy}</p>
              <p className="text-h-x5 text-grey-light line-through">
                {originalPrice}
              </p>
            </div>
            <Button
              ctaColor={CTA_COLORS.RED}
              onClick={onAddToCart}
              ctaText={isBundleUnavailable ? outOfStockButtonText : buttonText}
              disabled={loading || isBundleUnavailable}
              includeSpinner={loading}
              classes="disabled:bg-grey-dark disabled:border-grey-dark disabled:opacity-100"
              fullWidth
            />
          </div>
        </div>
      </div>
      <LearnMoreModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        title={title}
        description={learnMoreDescription}
        badge={badge}
        image={image}
        bundleImages={bundleImages}
        price={price}
        originalPrice={originalPrice}
        maxPrice={maxPrice}
        loading={loadingModal}
        buttonText={buttonText}
        buttonColor={CTA_COLORS.RED}
        handleClick={onModalAddToCart}
        product={playKitchenProduct}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        isBundleUnavailable={isBundleUnavailable}
        outOfStockButtonText={outOfStockButtonText}
      />
    </>
  );
}

export default HolidayBundlesCard;
